import type { Cents } from "../domain/money";

/**
 * THE REPORT MODEL (Phase 13, spec §42A).
 *
 * A report is structured DATA: an identity, sections of rows, disclosures
 * and the facts the reader needs in order to trust it. Nothing here knows
 * about React, the DOM, a PDF library or SQL, and no report ever performs
 * financial arithmetic of its own — the Phase 12 engine and the services
 * that feed it are the only source of a number.
 *
 * The same model is rendered three ways: the in-app preview, the printed
 * page (paginated by `paginate` below, so the preview IS the print), and
 * — via the tabular export model — CSV and XLSX.
 */

export type ReportValue =
  | { kind: "money"; cents: Cents }
  | { kind: "percent"; pctX100: number | null }
  | { kind: "text"; text: string }
  | { kind: "count"; value: number }
  | { kind: "link"; text: string; url: string; detail?: string }
  | { kind: "empty" };

export interface ReportRow {
  label: string;
  values: ReportValue[];
  /** Sub-label shown under the label — the "why" of the number. */
  note?: string;
  /** Emphasis for a subtotal/total row. */
  emphasis?: "total" | "subtotal" | "warning";
  /** Nesting depth for indented detail. */
  depth?: number;
  /**
   * Where this row came from, so an interactive preview can navigate to the
   * record (spec §42A). Print ignores it.
   */
  source?: ReportRowSource;
  /** Outbound link for the label itself (supplier product pages). */
  link?: { url: string; text?: string };
  /**
   * Spec §76K: this row is a SENTENCE, not a line item. A contractor scope
   * written in prose prints as a paragraph rather than a bold table label.
   */
  prose?: boolean;
  /**
   * Never end a page on this row: it heads a group (a work item and its
   * quote blanks, a Notes/Scope heading and its text) that must be read
   * together (spec §80-22).
   */
  keepWithNext?: boolean;
}

export type ReportRowSource =
  | { kind: "financing"; id: string }
  | { kind: "commitment"; id: string }
  | { kind: "feature"; id: string }
  | { kind: "expense"; id: string }
  | { kind: "purchase"; id: string }
  | { kind: "product"; id: string }
  | { kind: "changeOrder"; id: string }
  | { kind: "room"; id: string };

export interface ReportSection {
  title: string;
  /** Column headings for the value columns (label column is implicit). */
  columns: string[];
  rows: ReportRow[];
  note?: string;
  /** Heading for the implicit label column. */
  columnLabel?: string;
  /** Force a page break before this section when printing. */
  pageBreakBefore?: boolean;
}

export interface ReportIdentity {
  jobNumber: string;
  projectName: string;
  address: string;
  projectType: "renovation" | "new_construction";
  statusLabel: string;
}

export interface ReportModel {
  id: string;
  title: string;
  subtitle: string;
  sections: ReportSection[];
  /** Statements the reader needs in order to trust the numbers. */
  disclosures: string[];
  generatedAt: string;
  /** Header block for the printed page. */
  identity?: ReportIdentity;
  /** Missing or incomplete data, stated rather than hidden. */
  warnings?: string[];
  /** Small factual disclaimer (accountant-facing reports). */
  disclaimer?: string;
  /** How this run was configured, echoed on the page. */
  optionSummary?: string;
  /**
   * A vector drawing that IS the report (spec §60M): a floor plan sized in
   * real inches, so printing it produces a PDF a ruler agrees with. It is
   * never a screenshot, and the renderer only places it.
   */
  drawing?: { svg: string; caption: string; /** Physical sheet size, for the print @page rule. */ widthIn?: number; heightIn?: number };
  /**
   * Additional sheets after the first (spec §69-1): a multi-floor design
   * prints EVERY floor, each as its own vector page unless they legitimately
   * share one at the same scale.
   */
  drawings?: Array<{ svg: string; caption: string }>;
}

export const ACCOUNTING_DISCLAIMER =
  "Berrier Reno organizes project financial records and supporting documentation. " +
  "It does not determine tax deductibility or provide tax advice.";

/* ------------------------------------------------------------------ */
/* Tabular exports (CSV / XLSX)                                        */
/* ------------------------------------------------------------------ */

/**
 * Export cells are TYPED so a spreadsheet gets real numbers and a CSV gets
 * unambiguous text. One conceptual record per row; never two facts in one
 * cell.
 */
export type CellValue =
  | { kind: "text"; text: string }
  | { kind: "money"; cents: Cents }
  | { kind: "number"; value: number; decimals?: number }
  | { kind: "date"; iso: string }
  | { kind: "empty" };

export interface ExportColumn {
  key: string;
  label: string;
}

export interface ExportSheet {
  name: string;
  columns: ExportColumn[];
  rows: CellValue[][];
}

export interface ExportWorkbook {
  fileBaseName: string;
  sheets: ExportSheet[];
}

export const cell = {
  text: (text: string): CellValue => ({ kind: "text", text }),
  money: (cents: Cents): CellValue => ({ kind: "money", cents }),
  number: (value: number, decimals = 2): CellValue => ({ kind: "number", value, decimals }),
  date: (iso: string): CellValue => (iso ? { kind: "date", iso } : { kind: "empty" }),
  empty: (): CellValue => ({ kind: "empty" }),
};

/* ------------------------------------------------------------------ */
/* Pagination                                                          */
/* ------------------------------------------------------------------ */

export interface PageBlock {
  section: ReportSection;
  /** True when this block continues a section split across pages. */
  continued: boolean;
  /** True when the section ends on this page. */
  ends: boolean;
}

export interface ReportPage {
  pageNumber: number;
  blocks: PageBlock[];
  /** Warnings, disclosures and the disclaimer render on this page. */
  tail: boolean;
}

export interface PaginationOptions {
  /** Row units that fit on one page below the running header. */
  capacity?: number;
  /** Units the first page loses to the identity block. */
  firstPageOverhead?: number;
}

/**
 * Height cost of a row in PAGE UNITS.
 *
 * One unit is one plain table row. The figures are calibrated against the
 * rendered document (a plain row is 0.26in, a noted row 0.44in, a section
 * heading with its column header 0.51in, and the usable area of a Letter
 * page is 8.7in ≈ 33 units), so the estimate and the paper agree.
 */
function rowUnits(row: ReportRow): number {
  /*
   * A PROSE ROW WRAPS, AND THE PAGE HAS TO KNOW (spec §80-22).
   *
   * Prose rows render as .report-scope-prose, capped at 62ch — so a
   * 400-character scope paragraph is seven printed lines, not one. Counting
   * it as a single unit is how a document ends up longer on paper than its
   * own "Page X of Y" footer claims: the estimator under-counts, the page
   * overflows, and the browser silently spills into a page the model never
   * knew about. Wrapped lines are slightly taller than a table row
   * (line-height 1.5 against 0.26in), so each line costs a full unit.
   */
  const prose = row.prose ? Math.max(1, Math.ceil(row.label.length / 62)) : 1;
  if (!row.note) return prose;
  // A long note wraps onto further lines; assume ~60 characters per line.
  return prose + Math.min(3, Math.ceil(row.note.length / 60));
}

function sectionUnits(block: PageBlock): number {
  return (
    2 +
    (block.section.note ? 1 : 0) +
    block.section.rows.reduce((sum, row) => sum + rowUnits(row), 0)
  );
}

function tailUnits(model: ReportModel): number {
  const warnings = model.warnings?.length ? model.warnings.length + 2 : 0;
  const disclosures = model.disclosures.length ? model.disclosures.length + 2 : 0;
  const disclaimer = model.disclaimer ? 2 : 0;
  return warnings + disclosures + disclaimer;
}

/**
 * Lay the report out as fixed pages so the preview and the printed document
 * are the SAME thing: no clipped rows, no orphaned headings, and a column
 * header repeated (marked "continued") whenever a table crosses a page.
 *
 * Pure and deterministic — the same model always paginates identically,
 * which is what makes printed output testable.
 */
export function paginate(model: ReportModel, options: PaginationOptions = {}): ReportPage[] {
  const capacity = options.capacity ?? 32;
  const firstPageOverhead = options.firstPageOverhead ?? 3;
  const pages: ReportPage[] = [];
  let blocks: PageBlock[] = [];
  let used = 0;
  let pageNumber = 1;
  const room = () => capacity - (pageNumber === 1 ? firstPageOverhead : 0) - used;

  const flush = () => {
    pages.push({ pageNumber, blocks, tail: false });
    pageNumber += 1;
    blocks = [];
    used = 0;
  };

  for (const section of model.sections) {
    if (section.pageBreakBefore && blocks.length > 0) flush();
    let index = 0;
    let continued = false;
    for (;;) {
      const headingCost = 2 + (section.note && !continued ? 1 : 0);
      // A heading with no room for at least two rows under it starts a page.
      if (room() < headingCost + Math.min(2, section.rows.length - index) && blocks.length > 0) {
        flush();
        continue;
      }
      const available = Math.max(0, room() - headingCost);
      const taken: ReportRow[] = [];
      let cost = 0;
      while (index < section.rows.length && cost + rowUnits(section.rows[index]) <= available) {
        cost += rowUnits(section.rows[index]);
        taken.push(section.rows[index]);
        index += 1;
      }
      /*
       * KEEP WHAT BELONGS TOGETHER TOGETHER (spec §80-22). A work item's
       * heading and the three blanks a contractor writes in are one thing;
       * so is a Notes/Scope heading and its first line. A row marked
       * keepWithNext gives its whole group back to the next page rather
       * than leaving the group's head stranded at the bottom of this one.
       */
      while (taken.length > 0 && index < section.rows.length && taken[taken.length - 1].keepWithNext) {
        const back = taken.pop()!;
        cost -= rowUnits(back);
        index -= 1;
      }
      if (taken.length === 0 && blocks.length > 0 && index < section.rows.length) {
        // Nothing fitted once the group was preserved: start a fresh page.
        flush();
        continue;
      }
      const ends = index >= section.rows.length;
      blocks.push({
        section: { ...section, rows: taken, note: continued ? undefined : section.note },
        continued,
        ends,
      });
      used += headingCost + cost;
      continued = true;
      if (ends) break;
      flush();
    }
  }

  if (blocks.length > 0 || pages.length === 0) {
    pages.push({ pageNumber, blocks, tail: false });
  }

  // The tail goes on the last page when it fits, otherwise on a page of its
  // own — a disclosure is never half-cut.
  const last = pages[pages.length - 1];
  const lastUsed = last.blocks.reduce((sum, block) => sum + sectionUnits(block), 0);
  const overhead = last.pageNumber === 1 ? firstPageOverhead : 0;
  if (capacity - overhead - lastUsed >= tailUnits(model)) {
    last.tail = true;
  } else {
    pages.push({ pageNumber: last.pageNumber + 1, blocks: [], tail: true });
  }
  return pages;
}
